import React, { useImperativeHandle, useState, useRef, useContext, useEffect, useMemo } from "react";
import { Popup, PopupRef, Position, ToolbarItem } from 'devextreme-react/popup';
import 'devextreme-react/text-area';
import Form, { ButtonItem, EmptyItem, FormRef, GroupItem, Item, Label, SimpleItem } from 'devextreme-react/form';
import { FilterBuilder } from "devextreme-react";
import { FilterBuilderRef } from "devextreme-react/cjs/filter-builder";
// import ResponsiveBox, { Row, Col, Item, Location } from 'devextreme-react/responsive-box';
import { Field } from "devextreme/ui/filter_builder";
import { FiltriApi, FiltroDTO } from "../../data";
import { apiConfiguration } from "../../apiConfig";
import TenantContext from "../../contexts/tenantContext";
import FilterPreview from "./filterPreview";
import { useScreenSize } from "../../utils/media-query";


type Props = {
  onDataChanged?: () => void;
}

export type FilterEditPopupHandle = {
  edit: (filterId: string) => void,
  new: () => void,
}

// Mappa dei tipi per convertire i tipi dei campi nel formato corretto per FilterBuilder
const dataTypeMap: Record<string, "string" | "number" | "date" | "boolean" | "object" | undefined> = {
  string: "string",
  text: "string",
  number: "number",
  int: "number",
  integer: "number",
  float: "number",
  double: "number",
  decimal: "number",
  date: "date",
  datetime: "date",
  time: "date",
  boolean: "boolean",
  bool: "boolean",
  object: "object"
};

type state = {
  visible: boolean,
  filterId?: string
}

const FilterEditPopup: React.ForwardRefRenderFunction<FilterEditPopupHandle, Props> = (props, ref) => {
  const [state, setState] = useState<state>({ visible: false, filterId: undefined });
  const filterBuilderRef = useRef<FilterBuilderRef>(null);
  const { currentTenant } = useContext(TenantContext);
  const [fields, setFields] = useState<Field[]>([]);
  const [currentFilter, setCurrentFilter] = useState<FiltroDTO>();
  const [currentFilterExpression, setCurrentFilterExpression] = useState<[]>();
  const { isXSmall, isSmall } = useScreenSize();
  const [fullscreen, setFullscreen] = useState(isXSmall || isSmall);
  const formRef = useRef<FormRef>(null);

  useEffect(() => setFullscreen(isXSmall || isSmall), [isXSmall, isSmall]);

  function hide() {
    setState({ visible: false, filterId: undefined });
    setCurrentFilter(undefined);
  }

  useEffect(() => {
    async function loadFields() {
      const api = new FiltriApi(apiConfiguration);
      var result = await api.getFields({ tenantId: currentTenant.Id });

      var r = result.columns?.map(field => {
        // Il formato dei campi ricevuti potrebbe essere diverso, adattiamo i nomi
        const dataField = field.name;
        const rawType = (field.type || 'string').toLowerCase();

        // Mappa il tipo al formato corretto per FilterBuilder
        const dataType = dataTypeMap[rawType] || "string";

        // Assicuriamoci che il campo abbia un dataField valido
        if (!dataField) {
          console.warn('Campo senza dataField rilevato:', field);
          return null;
        }

        return {
          dataField,
          dataType,
          caption: field.name || dataField
        };
      }).filter(Boolean) as Field[];

      setFields(r);
    }

    loadFields();
  }, []);

  useEffect(() => {
    async function loadfiltro() {
      if (state?.filterId) {
        const api = new FiltriApi(apiConfiguration);
        var result = await api.getFiltroById({ tenantId: currentTenant.Id, filtroId: state.filterId });

        setCurrentFilter(result);
      }
    }

    loadfiltro();
  }, [state.filterId]);

  useEffect(() => {
    if (currentFilter?.espressione)
      setCurrentFilterExpression(JSON.parse(currentFilter.espressione));
    else
      setCurrentFilterExpression([]);
  }, [currentFilter]);

  useImperativeHandle(ref, () => ({
    async edit(filterId: string) {
      setState({
        visible: true,
        filterId: filterId
      });
    },
    async new() {
      setState({
        visible: true,
        filterId: undefined
      });
      setCurrentFilter({});
    },
  }));

  async function Confirm() {
    var validate = formRef.current?.instance().validate();

    if (validate?.isValid) {
      const api = new FiltriApi(apiConfiguration);
      var data = formRef.current?.instance().option("formData");

      if (data) {
        data.espressione = (!currentFilterExpression || currentFilterExpression?.length === 0) ? "" : JSON.stringify(currentFilterExpression);

        if (state.filterId) { // update
          await api.updateFiltro({
            tenantId: currentTenant.Id,
            filtroId: state.filterId,
            filtroUpdateDTO: currentFilter
          });
        }
        else {  // new
          await api.insertFiltro({
            tenantId: currentTenant.Id,
            filtroCreateDTO: currentFilter
          });
        }

        props.onDataChanged?.();
        hide();
      }
    }
  }

  return (<React.Fragment>

    <Popup
      visible={state.visible}
      dragEnabled={false}
      hideOnOutsideClick={true}
      showCloseButton={true}
      fullScreen={fullscreen}
      showTitle={true}
      onHidden={hide}
      title="Modifica filtro"
      container=".dx-viewport"
      width="80%"
      height="80%"
    >
      <Position
        at="center"
        my="center"
        of=""
      //collision="fit"
      />
      <ToolbarItem
        widget="dxButton"
        toolbar="top"
        location="after"
        visible={!(isXSmall || isSmall)}
        options={{
          icon: 'fullscreen',
          onClick: () => setFullscreen(!fullscreen),
        }}
      />
      <ToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        options={{
          text: 'Conferma',
          type: 'default',
          stylingMode: 'contained',
          onClick: Confirm
        }}
      />
      <ToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        options={{
          text: 'Annulla',
          type: 'normal',
          stylingMode: 'contained',
          onClick: hide
        }}
      />


      <Form id="form" ref={formRef} colCount={4} formData={currentFilter} labelMode='floating' >

        <Item dataField="nome" isRequired={true} colSpan={4} />

        <SimpleItem
          colSpan={2}
          render={(e) =>
            <FilterBuilder
              ref={filterBuilderRef}
              fields={fields}
              value={currentFilterExpression} // Usiamo lo stato locale anziché le props
              onValueChanged={e => setCurrentFilterExpression(e.value)}
              width="100%"
              allowHierarchicalFields={false}
            />
          }
        />
        <SimpleItem
          colSpan={2}
          render={(e) =>
            <FilterPreview expression={currentFilterExpression} />
          }
        />
      </Form>

      {/* <ResponsiveBox singleColumnScreen="sm xs" >
        <Row ratio={1} />
        <Col ratio={2} />
        <Col ratio={3} />
        <Item>
          <Location row={0} col={0} />
          <FilterBuilder
            ref={filterBuilderRef}
            fields={fields}
            value={currentFilterExpression} // Usiamo lo stato locale anziché le props
            onValueChanged={e => setCurrentFilterExpression(e.value)}
            width="100%"
            allowHierarchicalFields={false}
          />
        </Item>

        <Item>
          <Location row={0} col={1} rowspan={4} />
          <div className="spazio-sinistro-anteprima-filtro">
           
          </div>
        </Item>
      </ResponsiveBox> */}
    </Popup>

  </React.Fragment>
  );
}

export default React.forwardRef(FilterEditPopup);
